import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import "../../../../css/MassTexts.css";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AccountData,
  Event,
  MassText,
  MassTextAttachment,
  MassTextAttachmentType,
  MassTextType,
  SubSelectionType,
} from "@markit/common.types";
import {
  LessThanDate,
  MARKIT_DOCS,
  NON_SUBSCRIBER_REPLY_CAMPAIGN_ID,
  calculateDeliveryAttemptedRate,
  calculateTextMessageCreditsEstimate,
  calculateTextRemainingSendTime,
  getCategoryLabelName,
  getTimezone,
  handlePluralString,
  isDefaultTemplateAutomations,
  isDefaultPresetAutomations,
  massTextMessageConverter,
  relativeSendTimeLabel,
  subscribeProfileDefault,
  isInitialTriggerText,
  GetTime,
} from "@markit/common.utils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import {
  customPhoneMps,
  getMassTextAttachments,
  getMassTextSendRate,
  numRecipientsSentCompliance,
  getNumMassTextRecipients,
  appendedComplianceMessage,
} from "../../../../utils/textingUtils";
import { GetLongDate } from "../../../../utils/GetLongDate";
import { MassTextMessagingScreenType } from "../MassTextPanel";
import ProgressBar from "@ramonak/react-progress-bar";
import CustomLinkify from "../../../Links/CustomLinkify";
import { onSnapshot } from "../../../../firebase";
import {
  getCampaignMassTextRef,
  getUserData,
} from "../../../../utils/FirebaseUtils";
import { CircularProgress } from "@mui/material";
import { MassTextMessageCompositionProps } from "./MassTextMessageComposition";
import { getEventState } from "../../../../redux/slices/eventSlice";
import MassTextsLinkAnalytics from "./MassTextLinkAnalytics";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import TextCreditsTagModal from "../Message/TextCreditsTagModal";
import {
  calculateRevenueFromTextConversionsByAttachment,
  getMassTextAttachmentsLinkAnalytics,
  showTextConversions,
} from "../../../../utils/trackingUtils";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import SendingTextModal from "../Message/SendingTextModal";
import PopupModalMessageTag from "../../../Containers/PopupModalMessageTag";
import ComplianceMessageTag from "../../../Compliance/ComplianceMessageTag";
import SendRateModal from "../Message/SendRateModal";
import TextHoverButton from "../../../Buttons/TextHoverButton";
import { MassTextMediaAttachment } from "../Message/MassTextMediaAttachment";
import PopupModalContainer from "../../../Containers/PopupModalContainer";
import EventItemImage from "../../../DisplayItem/EventItem/EventItemImage";
import { FormatCurrency } from "../../../../utils/FormatCurrency";

type MassTextsSummaryProps = MassTextMessageCompositionProps;

const MassTextsSummary = (props: MassTextsSummaryProps) => {
  const {
    campaign,
    finalRecipients,
    massText,
    screen,
    setScreen,
    theme,
    loadingRecipients,
  } = props;
  const { account } = useSelector(getAccountState);
  const { accountData, savedQuestions } = account;
  const { events } = useSelector(getEventState);
  const { events: userEvents } = events;
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingLive, setLoadingLive] = useState<boolean>(true);
  const [liveMassText, setLiveMassText] = useState<MassText>(massText);
  const [massTextAttachments, setMassTextAttachments] = useState<
    MassTextAttachment[]
  >([]);
  const [deliveryAttemptedRate, setDeliveryAttemptedRate] = useState<number>(0);
  const [sendRate, setSendRate] = useState<number>(100);
  const [numOpensMap, setNumOpensMap] = useState<Map<string, number>>(
    new Map<string, number>()
  );
  const [numConversionsMap, setNumConversionsMap] = useState<
    Map<string, number>
  >(new Map<string, number>());
  const [conversionsRevenueMap, setConversionsRevenueMap] = useState<
    Map<string, number>
  >(new Map<string, number>());
  const [selectedAttachment, setSelectedAttachment] =
    useState<MassTextAttachment>();
  const liveQueuedRef = useRef<boolean | null>(null);
  const [sendingTextModal, setSendingTextModal] = useState(false);
  const [sendRateModal, setSendRateModal] = useState(false);
  const [liveRecipientsModal, setLiveRecipientsModal] = useState(false);
  const [numRecipients, setNumRecipients] = useState(finalRecipients.length);
  const [numRecipientsComplianceAdded, setNumRecipientsComplianceAdded] =
    useState(0);

  useEffect(() => {
    // Set ref to the live queued status to use in the setInterval
    liveQueuedRef.current = liveMassText.queued;
  }, [liveMassText.queued]);

  const styles = {
    sectionContainer: {
      paddingBlock: 14,
      backgroundColor: Colors.GRAY6,
      borderRadius: 12,
    },

    showcaseBody: {
      paddingInline: 14,
      paddingBlock: 7,
      gap: 7,
    },
  };

  const convertedMessage = useMemo(() => {
    return massTextMessageConverter(
      liveMassText.message,
      massTextAttachments,
      accountData.trustedSender
    );
  }, [liveMassText.message, massTextAttachments, accountData.trustedSender]);

  const convertedMessageWithAppendages = useMemo(
    () =>
      convertedMessage +
      (liveMassText.campaignId === NON_SUBSCRIBER_REPLY_CAMPAIGN_ID
        ? `dddddddddddddddd/u/${accountData.username}/subscribe` // placeholder for M4RKT subscribeProfileDefault num characters
        : isInitialTriggerText(liveMassText, campaign?.initialTextId) ||
          numRecipientsComplianceAdded / numRecipients > 0.5
        ? appendedComplianceMessage
        : ""),
    [
      accountData.username,
      campaign?.initialTextId,
      convertedMessage,
      liveMassText,
      numRecipients,
      numRecipientsComplianceAdded,
    ]
  );

  const isTriggerText = useMemo(
    () => massText.type === MassTextType.TRIGGERTEXT,
    [massText.type]
  );

  const foundMassTextEvent = useMemo(
    () => userEvents.find((event) => event.id === massText.eventRefId),
    [massText.eventRefId, userEvents]
  );

  const numTextCredits = useMemo(
    () =>
      calculateTextMessageCreditsEstimate(
        massText.message,
        accountData.trustedSender,
        massText.mediaUrl,
        foundMassTextEvent
      ),
    [
      accountData.trustedSender,
      foundMassTextEvent,
      massText.mediaUrl,
      massText.message,
    ]
  );

  const hasCategories = useMemo(
    () => massText.subSelectionIds.length > 0,
    [massText.subSelectionIds.length]
  );

  const isScheduledText = useMemo(
    () =>
      LessThanDate(new Date().toISOString(), liveMassText.sentAt) &&
      liveMassText.scheduled,
    [liveMassText.scheduled, liveMassText.sentAt]
  );

  /**
   * Text is finished sending if:
   * 1. firebase mass text queued, twilio messages queued (send rate), delivery rate is 100
   * 2. text queued and there were no recipients on the text (event text)
   * 3. it is a trigger text
   */
  const isTextFinishedSending = useMemo(
    () =>
      (liveMassText.queued &&
        sendRate !== -1 &&
        deliveryAttemptedRate === 100) ||
      liveMassText.queued === undefined ||
      (campaign?.eventId && liveMassText.queued && numRecipients === 0) ||
      liveMassText.type === MassTextType.TRIGGERTEXT,
    [
      sendRate,
      deliveryAttemptedRate,
      liveMassText.queued,
      liveMassText.type,
      campaign?.eventId,
      numRecipients,
    ]
  );

  const massTextAttachmentsToShow = useMemo(
    () =>
      massTextAttachments.filter(
        (attachment) =>
          attachment.massTextType !== MassTextAttachmentType.TICKET_LINK &&
          attachment.massTextType !== MassTextAttachmentType.CALENDAR_INVITE
      ),
    [massTextAttachments]
  );

  const categoryLabel = useMemo(() => {
    const tickets = foundMassTextEvent ? foundMassTextEvent.customTickets : [];
    return massText.subSelectionIds.length > 0
      ? massText.subSelectionIds.length > 1 ||
        (massText.subSelectionIds.length === 1 &&
          massText.subSelectionIds[0].type === SubSelectionType.FORM_QUESTION)
        ? "Custom Selection"
        : getCategoryLabelName(
            massText.subSelectionIds[0],
            tickets,
            savedQuestions
          )
      : "No Categories";
  }, [foundMassTextEvent, massText.subSelectionIds, savedQuestions]);

  // Calculates the send rate of the mass text only once the delivery is complete
  const checkAndUpdateSendRate = useCallback(
    async (massTextRecipients: number) => {
      const deliveryAttemptedRate = calculateDeliveryAttemptedRate(
        accountData.customer.phoneType,
        massTextRecipients,
        numTextCredits,
        massText.sentAt,
        customPhoneMps(accountData.uid)
      );
      setDeliveryAttemptedRate(deliveryAttemptedRate);
      if (deliveryAttemptedRate >= 100 && liveQueuedRef.current) {
        const massTextSendRate = await getMassTextSendRate(
          liveMassText,
          massTextRecipients
        );
        setSendRate(massTextSendRate);
        return massTextSendRate !== -1;
      }
      return false;
    },
    [
      accountData.customer.phoneType,
      accountData.uid,
      numTextCredits,
      massText,
      liveMassText,
    ]
  );

  const intervalRef = useRef<NodeJS.Timeout>();
  useAsyncOnMount(async () => {
    let finalUserData: AccountData = accountData;
    // for viewing other user's texting analytics
    if (massText.sentBy !== accountData.uid) {
      const userData = await getUserData(massText.sentBy);
      if (userData) {
        finalUserData = userData;
      }
    }

    const unsubscribe = onSnapshot(
      getCampaignMassTextRef(accountData.uid, massText.campaignId, massText.id),
      async (doc) => {
        const currMassText = doc.data();
        if (currMassText) {
          setLiveMassText(currMassText);
          const massTextAttachments = await getMassTextAttachments(
            finalUserData.uid,
            currMassText
          );
          setMassTextAttachments(massTextAttachments);

          const analyticsPromise = Promise.all(
            massTextAttachments.map(async (attachment) => {
              const { numOpens, numConversions } =
                await getMassTextAttachmentsLinkAnalytics(
                  finalUserData.uid,
                  currMassText,
                  attachment
                );
              return { attachmentId: attachment.id, numOpens, numConversions };
            })
          );
          const revenuePromise =
            calculateRevenueFromTextConversionsByAttachment(
              currMassText,
              massTextAttachments
            );
          const [analyticsByAttachment, conversionsRevenueByAttachment] =
            await Promise.all([analyticsPromise, revenuePromise]);

          const nextNumOpensMap = new Map<string, number>();
          const nextNumConversionsMap = new Map<string, number>();
          analyticsByAttachment.forEach(
            ({ attachmentId, numOpens, numConversions }) => {
              nextNumOpensMap.set(attachmentId, numOpens);
              nextNumConversionsMap.set(attachmentId, numConversions);
            }
          );
          setNumOpensMap(nextNumOpensMap);
          setNumConversionsMap(nextNumConversionsMap);
          setConversionsRevenueMap(conversionsRevenueByAttachment);

          if (campaign) {
            const [numSentCompliance, massTextRecipients] = await Promise.all([
              numRecipientsSentCompliance(
                finalUserData.uid,
                campaign.id,
                massText.id,
                massText.type
              ),
              getNumMassTextRecipients(
                finalUserData.uid,
                campaign,
                massText.id
              ),
            ]);
            setNumRecipients(massTextRecipients);
            setNumRecipientsComplianceAdded(numSentCompliance);

            // Ignore send rate and default 100% if trigger text (TODO: get actual calculation for trigger texts)
            if (!isTriggerText) {
              const isDeliveryComplete = await checkAndUpdateSendRate(
                massTextRecipients
              );

              // Clear any existing interval before potentially setting a new one
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = undefined;
              }
              if (!isDeliveryComplete) {
                intervalRef.current = setInterval(async () => {
                  const isDeliveryComplete = await checkAndUpdateSendRate(
                    massTextRecipients
                  );
                  if (isDeliveryComplete) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = undefined;
                  }
                }, 5000);
              }
            }
          }
        }
        setLoadingLive(false);
      }
    );

    setLoading(false);
    return () => {
      unsubscribe();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  });

  const renderEventItemPreview = useCallback(
    (attachment: MassTextAttachment, foundEvent: Event | undefined) => {
      return (
        <div className="AlignedRowSelect" style={{ gap: 8 }}>
          <EventItemImage item={foundEvent} size={40} />
          <div className="ColumnNormalSelect">
            <span style={{ fontSize: 14, fontWeight: "500" }}>
              {attachment.massTextType === MassTextAttachmentType.GENERIC_LINK
                ? "Form"
                : attachment.massTextType === MassTextAttachmentType.LINK
                ? `${foundEvent?.eventType ?? "Event"} Link`
                : attachment.massTextType ===
                  MassTextAttachmentType.TRACKING_LINK
                ? "Tracking Link"
                : attachment.massTextType}
            </span>
            <span className="bodySubtext">
              {foundEvent?.title ?? "Markit Link"}
            </span>
          </div>
        </div>
      );
    },
    []
  );

  const renderDateAndCredits = useMemo(
    () => (
      <>
        <span className="bodyMedium" style={theme?.PrimaryText}>
          {isTriggerText
            ? relativeSendTimeLabel(liveMassText.sentAtOffset, true)
            : isDefaultTemplateAutomations(massText.campaignId)
            ? relativeSendTimeLabel(liveMassText.sentAtOffset, false)
            : `${GetLongDate(liveMassText.sentAt, true, true, true)}, ${GetTime(
                liveMassText.sentAt
              )} ${getTimezone()}`}
        </span>
        <TextCreditsTagModal
          message={convertedMessageWithAppendages}
          mediaUrl={liveMassText.mediaUrl}
          event={foundMassTextEvent}
          showComplianceCreditNotice={
            numRecipientsComplianceAdded / numRecipients > 0.5
          }
        />
      </>
    ),
    [
      convertedMessageWithAppendages,
      foundMassTextEvent,
      isTriggerText,
      liveMassText.mediaUrl,
      liveMassText.sentAt,
      liveMassText.sentAtOffset,
      massText.campaignId,
      numRecipients,
      numRecipientsComplianceAdded,
      theme?.PrimaryText,
    ]
  );

  // 1. Scheduled
  // 1. Sent - Show the sent and send rate details
  // 2. In Progress - Show the progress bar while the messages finish getting delivered
  const renderTextRecipients = useMemo(
    () =>
      numRecipients > 0 || hasCategories || isTriggerText ? (
        <div className="ColumnNormal">
          <HorizontalDivider altMargin={14} />
          <div
            className="AlignedRowSpaced"
            style={{ paddingInline: 14, paddingBlock: 7 }}
          >
            <div className="AlignedRow" style={{ gap: 14 }}>
              {hasCategories ? (
                <span className="sectionTitle">{categoryLabel}</span>
              ) : (
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <span className="sectionTitle">
                    {loadingRecipients ? "--" : numRecipients}
                  </span>
                  <span className="bodySubtext">
                    {isTriggerText ? "Live " : ""}
                    {handlePluralString("Recipient", numRecipients)}
                  </span>
                  {isTriggerText ? (
                    <Icon
                      icon="mdi:information-outline"
                      height={14}
                      style={{ color: Colors.BLACK, cursor: "pointer" }}
                      onClick={() => {
                        setLiveRecipientsModal(true);
                      }}
                    />
                  ) : null}
                </div>
              )}
            </div>
            {hasCategories || numRecipients > 0 ? (
              <div
                className="AlignedRowSelect"
                onClick={() => {
                  setScreen(
                    MassTextMessagingScreenType.RECIPIENTS_SEND_SUMMARY
                  );
                }}
                style={{ gap: 2 }}
              >
                <span style={{ fontSize: 14 }}>See All</span>
                <Icon icon="ion:chevron-forward" height={18} />
              </div>
            ) : null}
          </div>
        </div>
      ) : null,
    [
      categoryLabel,
      numRecipients,
      hasCategories,
      isTriggerText,
      loadingRecipients,
      setScreen,
    ]
  );

  // Three cases: 1. Scheduled Text, 2. Sent Text, 3. Sending Text
  const renderTextDetails = useMemo(
    () =>
      massText.type === MassTextType.MASSTEXT && isScheduledText ? (
        <div className="ColumnNormal" style={styles.sectionContainer}>
          <div className="ColumnNormal" style={styles.showcaseBody}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <Icon
                icon="ion:timer"
                height={16}
                style={{ color: Colors.BLUE5 }}
              />
              <span className="sectionTitle" style={{ color: Colors.BLUE5 }}>
                Scheduled
              </span>
            </div>
            {renderDateAndCredits}
          </div>
          {renderTextRecipients}
        </div>
      ) : isTextFinishedSending ? (
        <div className="ColumnNormal" style={styles.sectionContainer}>
          <div className="ColumnNormal" style={styles.showcaseBody}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <Icon
                icon={
                  isTriggerText
                    ? "ion:return-down-forward-outline"
                    : "ion:paper-plane"
                }
                height={16}
              />
              <span className="sectionTitle">
                {isTriggerText && campaign ? "Auto Text" : "Sent"}
              </span>
            </div>
            {renderDateAndCredits}
          </div>
          {campaign && isDefaultPresetAutomations(campaign) ? null : (
            <>
              <HorizontalDivider altMargin={14} />
              <div className="AlignedRow" style={styles.showcaseBody}>
                <span className="sectionTitle">{sendRate}%</span>
                <span className="bodySubtext">Send Rate</span>
                <Icon
                  icon="mdi:information-outline"
                  height={16}
                  onClick={() => setSendRateModal(true)}
                  style={{
                    color: sendRate < 100 ? Colors.RED1 : Colors.GRAY1,
                    cursor: "pointer",
                  }}
                />
              </div>
            </>
          )}
          {numRecipients > 0 ? renderTextRecipients : null}
        </div>
      ) : (
        <div
          className="ColumnNormal"
          style={{ ...styles.sectionContainer, gap: 7 }}
        >
          <div className="ColumnNormal" style={{ paddingInline: 14, gap: 10 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <Icon
                icon={"ion:paper-plane"}
                height={14}
                style={{ color: Colors.BLUE5 }}
              />
              <span className="bodyMedium" style={{ color: Colors.BLUE5 }}>
                {!liveMassText.queued ? "Queued..." : "Sending..."}
              </span>
            </div>
            <ProgressBar
              completed={
                !liveMassText.queued ? 3 : Math.max(deliveryAttemptedRate, 3)
              }
              maxCompleted={100}
              isLabelVisible={false}
              bgColor={Colors.BLUE5}
              baseBgColor={Colors.WHITE}
              height="16px"
              width="100%"
              borderRadius="4px"
            />
            <div className="ColumnNormal" style={{ gap: 4 }}>
              {liveMassText.queued ? (
                <>
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    <span className="bodyMedium">Estimated Remaining Time</span>
                    <span
                      className="bodyMedium"
                      style={{ color: Colors.GREEN2 }}
                    >
                      {calculateTextRemainingSendTime(
                        numTextCredits * numRecipients,
                        accountData.customer.phoneType,
                        liveMassText.sentAt,
                        customPhoneMps(accountData.uid)
                      )}
                    </span>
                  </div>
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <span className="smallBodySubtext">
                      Texts with more recipients may take longer to send.
                    </span>
                    <Icon
                      icon="mdi:information-outline"
                      height={13}
                      style={{ cursor: "pointer", color: Colors.GRAY1 }}
                      onClick={() => setSendingTextModal(true)}
                    />
                  </div>
                </>
              ) : null}
              <div className="AlignedRow" style={{ gap: 7 }}>
                <span className="bodySmallRegular">
                  Want faster send times?
                </span>
                <TextHoverButton
                  text="Learn More"
                  onPress={() => window.open(MARKIT_DOCS + "/sendingtimes")}
                  icon={<Icon icon="ion:chevron-forward" height={12} />}
                  iconPosition="right"
                  textStyles={{ color: Colors.BLACK }}
                />
              </div>
            </div>
          </div>
          {renderTextRecipients}
        </div>
      ),
    [
      accountData.customer.phoneType,
      accountData.uid,
      campaign,
      deliveryAttemptedRate,
      isScheduledText,
      isTextFinishedSending,
      isTriggerText,
      liveMassText.queued,
      liveMassText.sentAt,
      massText.type,
      numRecipients,
      numTextCredits,
      renderDateAndCredits,
      renderTextRecipients,
      sendRate,
      styles.sectionContainer,
      styles.showcaseBody,
    ]
  );

  const renderShowcaseBox = (attachment: MassTextAttachment) => {
    const foundEvent = userEvents.find(
      (event) => event.id === attachment.eventId
    );
    const showConversions = showTextConversions(foundEvent, attachment);
    return (
      <div
        key={attachment.id}
        onClick={() => {
          setSelectedAttachment(attachment);
          setScreen(MassTextMessagingScreenType.LINK_ANALYTICS);
        }}
        className="MassTextsSectionContainer"
        style={{
          paddingBlock: 14,
          paddingInline: 0,
          pointerEvents: isTextFinishedSending ? "all" : "none",
        }}
      >
        <div className="AlignedRowSpacedSelect" style={{ paddingInline: 14 }}>
          {renderEventItemPreview(attachment, foundEvent)}
          {isTextFinishedSending ? (
            <Icon
              icon="ion:chevron-forward-outline"
              height={20}
              style={{ color: Colors.GRAY1 }}
            />
          ) : null}
        </div>
        {isTextFinishedSending ? (
          <>
            <HorizontalDivider altMargin={14} />
            <div className="AlignedRow" style={{ gap: 24, paddingInline: 14 }}>
              <div className="ColumnNormal">
                <span className="sectionTitle">
                  {numOpensMap.get(attachment.id) || 0}
                </span>
                <span className="smallBodySubtext">
                  Click
                  {numOpensMap.get(attachment.id) === 1 ? "" : "s"}
                </span>
              </div>
              {showConversions ? (
                <div className="AlignedRow" style={{ gap: 24 }}>
                  <div className="ColumnNormal">
                    <span className="sectionTitle">
                      {numConversionsMap.get(attachment.id) || 0}
                    </span>
                    <span className="smallBodySubtext">
                      Conversion
                      {numConversionsMap.get(attachment.id) === 1 ? "" : "s"}
                    </span>
                  </div>
                  {(conversionsRevenueMap.get(attachment.id) || 0) > 0 ? (
                    <div className="ColumnNormal">
                      <span className="sectionTitle">
                        {FormatCurrency(
                          conversionsRevenueMap.get(attachment.id) || 0
                        )}
                      </span>
                      <span className="smallBodySubtext">Revenue</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  const renderMessage = useMemo(
    () => (
      <div className="ColumnCenter" style={{ gap: 14 }}>
        <span className="sectionTitle" style={theme?.PrimaryText}>
          Message
        </span>
        <CustomLinkify>
          <span
            className="bodySubtext"
            style={{
              whiteSpace: "break-spaces", // TODO (jonathan): text here to fix overflow text
            }}
          >
            {convertedMessage}
          </span>
        </CustomLinkify>
        <div style={{ paddingBlock: 14 }}>
          {massText.campaignId === NON_SUBSCRIBER_REPLY_CAMPAIGN_ID ? (
            <PopupModalMessageTag
              message={subscribeProfileDefault(accountData.username)}
              popupTitle="Profile Link Included"
              popupSubtext="The link to subscribe to your profile will be automatically appended to the end of your message so that people can subscribe."
            />
          ) : isInitialTriggerText(massText, campaign?.initialTextId) ||
            numRecipientsComplianceAdded > 0 ? (
            <ComplianceMessageTag
              alternateComplianceMessage={
                massText.type === MassTextType.TRIGGERTEXT
                  ? `This additional default message ${
                      massText.eventRefId ? "may" : "will"
                    } be added to this text when sent for the first time.`
                  : `${numRecipientsComplianceAdded} of your selected recipients received a compliance message. For these recipients, this additional default message was added to your text.`
              }
              alternateContactCardMessage={
                massText.type === MassTextType.MASSTEXT
                  ? `${numRecipientsComplianceAdded} of your selected recipients received their first text from you. A contact card was added and used 3 extra credits per recipient.`
                  : undefined
              }
              mayBeIncluded={massText.eventRefId !== ""}
              pastText
            />
          ) : undefined}
        </div>
        {massText.mediaUrl ? (
          <MassTextMediaAttachment mediaUrl={massText.mediaUrl} />
        ) : null}
      </div>
    ),
    [
      accountData.username,
      campaign?.initialTextId,
      convertedMessage,
      massText,
      numRecipientsComplianceAdded,
      theme?.PrimaryText,
    ]
  );

  return (
    <>
      {!selectedAttachment ||
      screen === MassTextMessagingScreenType.SEND_SUMMARY ? (
        <div
          className="ColumnNormal"
          style={{ gap: 20, padding: 20, height: "100%" }}
        >
          {!loadingLive && !loading ? (
            <>
              {renderTextDetails}
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <span className="sectionTitle">
                  {isTextFinishedSending ? "Link Analytics" : "Links"}
                </span>
                {massTextAttachmentsToShow.length > 0 ? (
                  massTextAttachmentsToShow.map((attachment) =>
                    renderShowcaseBox(attachment)
                  )
                ) : (
                  <div
                    style={{
                      ...styles.sectionContainer,
                      paddingInline: 14,
                      paddingBlock: 24,
                    }}
                  >
                    <span className="bodySubtext">No Links to Show</span>
                  </div>
                )}
              </div>
              {renderMessage}
              {/** TODO: Hidden after new automation stuff, re-add it down the road when we want this functionality back */}
              {/* {hasCategories ? (
                <MassTextCatchUp
                  massText={liveMassText}
                  catchUpChecked={liveMassText.catchUpEnabled}
                  setCatchUpChecked={(catchUp: boolean) =>
                    updateMassTextSettings({ catchUpEnabled: catchUp })
                  }
                  theme={theme}
                />
              ) : null} */}
              <div style={{ paddingBlock: 80 }} />
            </>
          ) : (
            <div className="Centering" style={{ height: "80%" }}>
              <CircularProgress
                style={{ color: Colors.GRAY1, marginTop: 14 }}
                size={24}
              />
            </div>
          )}
        </div>
      ) : (
        <MassTextsLinkAnalytics
          massText={liveMassText}
          massTextAttachment={selectedAttachment}
        />
      )}
      <SendingTextModal
        modalVisible={sendingTextModal}
        setModalVisible={setSendingTextModal}
        totalNumCredits={numTextCredits * numRecipients}
        sentAt={liveMassText.sentAt}
      />
      <SendRateModal
        modalVisible={sendRateModal}
        setModalVisible={setSendRateModal}
        sendRate={sendRate}
      />
      {liveRecipientsModal ? (
        <PopupModalContainer
          headerComp={"Auto Text Recipients"}
          valueComp={
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span className="bodyMedium">What are live recipients?</span>
              <span className="bodySubtext">
                Live recipients are the people who received this Auto Text. If
                an Auto Text was set up after some people already trigger this
                automation, they will not be considered &quot;live
                recipients&quot; since they would not have received this text.
              </span>
            </div>
          }
          closeOnOutsidePress
          closeModal={() => setLiveRecipientsModal(false)}
        />
      ) : null}
    </>
  );
};

export default MassTextsSummary;
